export type Token = 'DPN' | 'DPS';
export type Candle = { time: number; open: number; high: number; low: number; close: number };
export type Round = {
  id: string;
  startAt: number;
  endAt: number;
  status: 'scheduled' | 'open' | 'pending' | 'resolved' | 'cancelled';
  referencePrice?: number;
  finalPrice?: number;
  result?: 'up' | 'down';
  markets: Partial<Record<Token, { eventId: string; ready: boolean; marketAddress?: string }>>;
};
export type RoundFeed = { serverTime: number; timezone: string; rounds: Round[] };
export type CandleFeed = {
  roundId: string;
  source: string;
  updatedAt: number;
  staleAfterSeconds: number;
  candles: Candle[];
};
const ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const epoch = (v: unknown): v is number => Number.isInteger(v) && Number(v) >= 0 && Number(v) < 1e11;
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
/**
 * Phase of a round. The engine's `status` decides (it writes open iff startAt <= now < endAt and
 * pending after endAt, every ~5s tick); the page clock only fills in a 'scheduled' round whose
 * start or end passed before the engine's next tick.
 */
export const roundPhase = (round: Round, now: number) => {
  if (round.status !== 'scheduled') return round.status;
  if (now < round.startAt) return 'upcoming';
  return now < round.endAt ? 'open' : 'pending';
};
export const selectDefaultRound = (rounds: Round[], now: number) => {
  const ordered = [...rounds].sort((a, b) => a.startAt - b.startAt);
  return (
    ordered.find(r => r.startAt <= now && now < r.endAt && r.status !== 'cancelled') ??
    ordered.find(r => r.startAt > now && r.status !== 'cancelled') ??
    ordered.at(-1)
  );
};
/**
 * Round the page follows when the reader has not picked one: the open round as soon as there
 * is one (one the engine marks open first, then one only the clock opens); otherwise it stays on
 * `previous` (an ended round keeps showing its settlement, never jumping to a round that has not
 * started), and re-picks the default when `previous` left the feed.
 */
export const followRound = (rounds: Round[], now: number, previous: string) =>
  rounds.find(r => r.status === 'open')?.id ??
  rounds.find(r => roundPhase(r, now) === 'open')?.id ??
  (rounds.some(r => r.id === previous) ? previous : (selectDefaultRound(rounds, now)?.id ?? ''));
export const roundWindow = (rounds: Round[], selected: string, count = 7) => {
  const index = Math.max(
    0,
    rounds.findIndex(r => r.id === selected)
  );
  const start = Math.min(Math.max(0, index - Math.floor(count / 2)), Math.max(0, rounds.length - count));
  return rounds.slice(start, start + count);
};
export const aggregateCandles = (candles: Candle[], interval: number): Candle[] => {
  if (!Number.isInteger(interval) || interval < 60) throw new Error('Invalid candle interval');
  const unique = new Map<number, Candle>();
  for (const c of candles) {
    if (
      !epoch(c.time) ||
      ![c.open, c.high, c.low, c.close].every(positive) ||
      c.low > Math.min(c.open, c.close) ||
      c.high < Math.max(c.open, c.close) ||
      c.low > c.high
    )
      throw new Error('Invalid OHLC');
    unique.set(c.time, c);
  }
  const buckets = new Map<number, Candle>();
  for (const c of [...unique.values()].sort((a, b) => a.time - b.time)) {
    const time = Math.floor(c.time / interval) * interval;
    const prior = buckets.get(time);
    buckets.set(
      time,
      prior
        ? { time, open: prior.open, high: Math.max(prior.high, c.high), low: Math.min(prior.low, c.low), close: c.close }
        : { ...c, time }
    );
  }
  return [...buckets.values()];
};
export const parseRoundFeed = (value: unknown): RoundFeed => {
  const data = value as RoundFeed;
  if (!data || !epoch(data.serverTime) || typeof data.timezone !== 'string' || !Array.isArray(data.rounds))
    throw new Error('Invalid round feed');
  new Intl.DateTimeFormat('en', { timeZone: data.timezone });
  const ids = new Set<string>();
  for (const r of data.rounds) {
    if (
      !r ||
      typeof r.id !== 'string' ||
      !r.id ||
      ids.has(r.id) ||
      !epoch(r.startAt) ||
      !epoch(r.endAt) ||
      r.startAt >= r.endAt ||
      !['scheduled', 'open', 'pending', 'resolved', 'cancelled'].includes(r.status) ||
      !r.markets
    )
      throw new Error('Invalid round');
    for (const token of ['DPN', 'DPS'] as const) {
      const m = r.markets[token];
      if (m && (typeof m.eventId !== 'string' || !m.eventId || typeof m.ready !== 'boolean'))
        throw new Error('Invalid market');
      // On-chain pool address is optional (the market may not exist yet) but never half-valid.
      if (m && m.marketAddress !== undefined && !ADDRESS.test(String(m.marketAddress)))
        throw new Error('Invalid market address');
    }
    if (
      (r.referencePrice !== undefined && !positive(r.referencePrice)) ||
      (r.finalPrice !== undefined && !positive(r.finalPrice))
    )
      throw new Error('Invalid reference price');
    ids.add(r.id);
  }
  return { ...data, rounds: [...data.rounds].sort((a, b) => a.startAt - b.startAt) };
};
export const parseCandleFeed = (value: unknown, roundId: string): CandleFeed => {
  const data = value as CandleFeed;
  if (
    !data ||
    data.roundId !== roundId ||
    !data.source ||
    !epoch(data.updatedAt) ||
    !positive(data.staleAfterSeconds) ||
    !Array.isArray(data.candles)
  )
    throw new Error('Invalid candle feed');
  return { ...data, candles: aggregateCandles(data.candles, 60) };
};
export const formatTime = (time: number, timezone: string) => {
  return new Intl.DateTimeFormat('zh-CN', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false }).format(
    time * 1000
  );
};
const TIMEZONE_LABELS: Record<string, string> = { 'Asia/Singapore': 'SGT' };
export const timezoneLabel = (timezone: string) => {
  return TIMEZONE_LABELS[timezone] ?? timezone;
};
/** A BTC price as the 5-minute page prints it: en-US grouping, two decimals, no currency sign. */
export const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
/**
 * A round's market for `token` can be traded on the page once it is deployed and published
 * (ready + pool address), including before the window starts, and until endAt. Stale candles
 * and a missing reference price deliberately do not matter here.
 */
export const isTradable = (round: Round, token: Token, now: number) => {
  const market = round.markets[token];
  // The engine's status closes trading; the clock is an early-close backstop. The pool's end_time is
  // endAt - 1s (promethex-mind/tools/dpin-ops/dpin_5m.py build_5m_payload: `rnd["endAt"] - 1`), so
  // the backstop closes there rather than at endAt.
  return (
    !['pending', 'resolved', 'cancelled'].includes(round.status) &&
    !!market?.ready &&
    !!market.marketAddress &&
    now < round.endAt - 1
  );
};
